import React, { useState, useEffect, useMemo } from 'react';
import { auth } from '../../services/firebase.js';
import { updateCompany, uploadLogo } from '../../services/companyService.js';
import AdminLayout from './AdminLayout.js';
import LoadingSpinner from '../Common/LoadingSpinner.js';
import LogoTab from './WebsiteSettings/LogoTab.js';
import ContactTab from './WebsiteSettings/ContactTab.js';
import HoursTab from './WebsiteSettings/HoursTab.js';
import SoMeTab from './WebsiteSettings/SoMeTab.js';
import DesignTab from './WebsiteSettings/DesignTab.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase.js';

const WebsiteSettings = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [company, setCompany] = useState(null);
    const [activeTab, setActiveTab] = useState('logo');

    // Get cvr from company data
    const cvr = company?.cvr;
    const user = auth.currentUser;

    const orderedDays = useMemo(() => [
        'Mandag',
        'Tirsdag',
        'Onsdag',
        'Torsdag',
        'Fredag',
        'Lørdag',
        'Søndag'
    ], []);

    const defaultHours = useMemo(() => ({
        Mandag: '07:00 - 16:00',
        Tirsdag: '07:00 - 16:00',
        Onsdag: '07:00 - 16:00',
        Torsdag: '07:00 - 16:00',
        Fredag: '07:00 - 15:00',
        Lørdag: 'Lukket',
        Søndag: 'Lukket'
    }), []);

    const [formData, setFormData] = useState({
        phone: '',
        email: '',
        address: '',
        zipcode: '',
        city: '',
        hours: defaultHours,
        showHours: true, // Add this line
        socialMedia: {
            facebook: '',
            instagram: '',
            linkedin: '',
            tiktok: ''
        },
        domain: {
            subdomain: '',
            custom: ''
        },
        theme: 'amber', // Standard
    });
    
    useEffect(() => {
        const fetchCompany = async () => {
            if (user?.email) {
                try {
                    // Query company by companyEmail
                    const companiesRef = collection(db, 'companies');
                    const q = query(companiesRef, where('companyEmail', '==', user.email));
                    const querySnapshot = await getDocs(q);
                    
                    if (!querySnapshot.empty) {
                        const companyData = { 
                            id: querySnapshot.docs[0].id, 
                            ...querySnapshot.docs[0].data() 
                        };
                        setCompany(companyData);
                        setFormData({
                            phone: companyData.phone || '',
                            email: companyData.email || '',
                            address: companyData.address || '',
                            zipcode: companyData.zipcode || '',
                            city: companyData.city || '',
                            hours: companyData.hours || defaultHours,
                            socialMedia: companyData.socialMedia || {
                                facebook: '',
                                instagram: '',
                                linkedin: '',
                                tiktok: ''
                            },
                            domain: companyData.domain || {
                                subdomain: '',
                                custom: ''
                            },
                            theme: companyData.theme || 'amber'
                        });
                    }
                } catch (err) {
                    setError('Kunne ikke hente firma information');
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchCompany();
    }, [user, defaultHours]);

    const handleLogoUpload = async (e) => {
        const file = e.target.files[0];
        if (!file || !cvr) return;

        setSaving(true);
        try {
            const logoUrl = await uploadLogo(file, cvr);
            await updateCompany(cvr, { logo: logoUrl });
            
            setCompany(prev => ({ ...prev, logo: logoUrl }));
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Kunne ikke uploade logo');
        } finally {
            setSaving(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        
        try {
            await updateCompany(cvr, formData);
            setSuccess(true);
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            setError('Kunne ikke gemme ændringer');
        } finally {
            setSaving(false);
        }
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => {
            const newState = { ...prev };
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                newState[parent] = {
                    ...newState[parent],
                    [child]: value
                };
            } else {
                newState[field] = value;
            }
            return newState;
        });
    };

    const handleHoursChange = (day, value) => {
        setFormData(prev => ({
            ...prev,
            hours: {
                ...prev.hours,
                [day]: value
            }
        }));
    };

    const tabs = [
        { 
            id: 'logo', 
            name: 'Logo',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            )
        },
        { 
            id: 'contact', 
            name: 'Kontakt',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
            )
        },
        { 
            id: 'hours', 
            name: 'Åbningstider',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            )
        },
        { 
            id: 'social', 
            name: 'Sociale medier',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                </svg>
            )
        },
        { 
            id: 'design', 
            name: 'Design',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>
                </svg>
            )
        },
        { 
            id: 'domain', 
            name: 'Domæne',
            icon: (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1V4z" />
                </svg>
            )
        },
    ];

    const renderTabContent = () => {
        switch (activeTab) {
            case 'logo':
                return <LogoTab 
                    company={company} 
                    onLogoUpload={handleLogoUpload}
                />;
            case 'contact':
                return <ContactTab 
                    formData={formData}
                    onChange={handleFormChange}
                />;
            case 'hours':
                return <HoursTab 
                    formData={formData}
                    orderedDays={orderedDays}
                    onChange={handleHoursChange}
                />;
            case 'social':
                return <SoMeTab 
                    formData={formData}
                    onChange={handleFormChange}
                />;
            case 'domain':
                return <DomainTab 
                    formData={formData}
                    onChange={handleFormChange}
                    company={company}
                />;
            case 'design':
                return <DesignTab formData={formData} onChange={handleFormChange} />;
            default:
                return null;
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <h1 className="text-2xl font-semibold text-gray-900 font-montserrat mb-6">Hjemmeside indstillinger</h1>
                
                <form onSubmit={handleSave} className="space-y-6">
                    {/* Tabs */}
                    <div className="border-b border-gray-200">
                        <nav className="-mb-px flex space-x-8">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    type="button"
                                    onClick={() => setActiveTab(tab.id)} // Only change active tab
                                    className={`
                                        flex items-center py-4 px-1 border-b-2 font-medium text-sm
                                        ${activeTab === tab.id
                                            ? 'border-brand-dark text-brand-dark'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    {tab.icon}
                                    <span className="ml-2">{tab.name}</span>
                                </button>
                            ))}
                        </nav>
                    </div>

                    {/* Tab content */}
                    <div className="mt-6">
                        {renderTabContent()}
                    </div>

                    {/* Save Button and Status Messages */}
                    <div className="flex items-center justify-end space-x-4">
                        {error && (
                            <p className="text-sm text-red-600">{error}</p>
                        )}
                        {success && (
                            <p className="text-sm text-green-600">Ændringer blev gemt!</p>
                        )}
                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-brand-dark hover:bg-brand-dark/90 text-white px-4 py-2 rounded-md disabled:opacity-50"
                        >
                            {saving ? (
                                activeTab === 'logo' ? 'Uploader logo...' : 'Gemmer...'
                            ) : (
                                'Gem ændringer'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </AdminLayout>
    );
};

const DomainTab = ({ formData, onChange, company }) => {
    const cvr = company?.cvr;
    const baseUrl = "håndværkernet.dk";
    
    return (
        <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium font-montserrat text-gray-900 mb-4">Domæne indstillinger</h2>
            <div className="space-y-6">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Dit nuværende website</label>
                    <p className="text-gray-600">https://{baseUrl}/{cvr}</p>
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700">Eget domæne</label>
                    <input
                        type="text"
                        value={formData.domain.custom}
                        onChange={(e) => onChange('domain.custom', e.target.value)}
                        className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2"
                        placeholder="www.mitdomaene.dk"
                    />
                    <div className="mt-4 bg-brand-light/20 p-4 rounded-lg border border-brand-light">
                        <h3 className="text-sm font-medium text-brand-dark mb-2">Sådan peger du dit domæne til din nye hjemmeside:</h3>
                        <ol className="list-decimal ml-4 space-y-2 text-sm text-brand-dark/90">
                            <li>Log ind hos din domæneudbyder (fx GoDaddy, One.com eller DK Hostmaster)</li>
                            <li>Find DNS indstillinger eller DNS management</li>
                            <li>Tilføj følgende DNS records:
                                <div className="mt-2 bg-white p-3 rounded border border-brand-light font-mono text-xs">
                                    <p>A-record: <span className="font-semibold">199.36.158.100</span></p>
                                    <p>TXT-record: <span className="font-semibold">hosting-site=craftsmanweb</span></p>
                                </div>
                            </li>
                            <li>Vent op til 24 timer på at ændringerne træder i kraft</li>
                        </ol>
                        <p className="mt-3 text-sm text-brand-dark/90">
                            Når dit domæne er sat op, vil det automatisk vise din hjemmeside og alle undersider.
                            For eksempel vil www.mitdomaene.dk/kontakt pege på {baseUrl}/{cvr}/kontakt
                        </p>
                        <p className="mt-3 text-sm text-brand-dark/80">
                            Har du brug for hjælp? Kontakt os på <a href="mailto:kontakt@haandvaerkernet.dk">kontakt@haandvaerkernet.dk</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WebsiteSettings;